import { useEffect, useRef, useState } from 'react';

export const useExpandAnimation = <K extends HTMLElement>(expanded?: boolean) => {
  const [isExpanded, setExpanded] = useState(expanded);
  const isInitialMount = useRef(true);
  const contentRef = useRef<K>(null);

  const toggleExpanded = () => {
    if (isExpanded) {
      if (contentRef && contentRef.current) {
        const realHeight = contentRef.current.scrollHeight;
        contentRef.current.classList.add('overflow-hidden');
        contentRef.current.style.height = realHeight + 'px';
        requestAnimationFrame(() => {
          if (contentRef && contentRef.current) contentRef.current.style.height = 0 + 'px';
        });
        contentRef.current.addEventListener(
          'transitionend',
          () => {
            setExpanded(false);
          },
          { once: true },
        );
      }
    } else setExpanded(true);
  };

  useEffect(() => {
    if (!contentRef.current) return;

    const node = contentRef.current;

    if (isExpanded) {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        node.classList.remove('h-0');
        node.style.height = '';
        node.classList.remove('overflow-hidden');
        return;
      }
      isInitialMount.current = false;
      const realHeight = node.scrollHeight;
      const rafId = requestAnimationFrame(() => {
        if (contentRef.current) contentRef.current.style.height = realHeight + 'px';
      });
      const handleTransitionEnd = () => {
        if (contentRef.current) {
          contentRef.current.classList.remove('h-0');
          contentRef.current.style.height = '';
          contentRef.current.classList.remove('overflow-hidden');
        }
      };
      node.addEventListener('transitionend', handleTransitionEnd, { once: true });

      return () => {
        cancelAnimationFrame(rafId);
        node.removeEventListener('transitionend', handleTransitionEnd);
      };
    }
    isInitialMount.current = false;
  }, [isExpanded]);

  return { isExpanded, contentRef, toggleExpanded };
};
